<template>
    <div class="seedream-page">
        <!-- Image Generator Section -->
        <section ref="generatorSection" class="generator-section">
            <div class="container">
                <div class="generator-header">
                    <h1 class="generator-title">
                        {{ t('seedream.generatorTitle') }}
                    </h1>
                    <p class="generator-subtitle">
                        {{ t('seedream.generatorSubtitle') }}
                    </p>
                </div>

                <ImageGenerator model-name="Seedream 4.0" :default-tab="1" @generate="handleImageGenerate"
                    model-key="SEEDREAM" />
            </div>
        </section>

        <!-- Core Features Section -->
        <section class="core-features-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('seedream.coreFeaturesTitle') }}
                    </h2>
                    <p class="section-subtitle">
                        {{ t('seedream.coreFeaturesSubtitle') }}
                    </p>
                </div>

                <div class="features-showcase-container">
                    <!-- Unlimited Artistic Styles Feature -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('seedream.unlimitedStylesTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('seedream.unlimitedStylesDesc') }}
                                </p>
                            </div>
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/cms/c761c163fa45564c121e631464172453.webp"
                                        :alt="t('seedream.unlimitedStylesAlt')" class="feature-image-preview"
                                        fetchpriority="high" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Efficient Batch Processing Feature (reverse layout) -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/cms/6cd05544bc7f589961aff5cac39c4f1e.webp"
                                        :alt="t('seedream.batchProcessingAlt')" class="feature-image-preview"
                                        loading="lazy" />
                                </div>
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('seedream.batchProcessingTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('seedream.batchProcessingDesc') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Precise Prompt Editing Feature -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t('seedream.precisePromptEditingTitle')
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('seedream.precisePromptEditingDesc') }}
                                </p>
                            </div>
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/cms/9d3d8c8b5d1c27a5b5e6725a8c8afb59.webp" :alt="t(
                                        'seedream.precisePromptEditingAlt'
                                    )
                                        " class="feature-image-preview" loading="lazy" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Unified Visual Identity Feature (reverse layout) -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/cms/3b33eb86e7601bdce9bab741486bce40.webp"
                                        :alt="t('seedream.unifiedIdentityAlt')" class="feature-image-preview"
                                        loading="lazy" />
                                </div>
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('seedream.unifiedIdentityTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('seedream.unifiedIdentityDesc') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Accurate In-Image Text Feature -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('seedream.accurateTextTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('seedream.accurateTextDesc') }}
                                </p>
                            </div>
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/cms/8494c034b25a51d77bf2007b21ba5871.webp"
                                        :alt="t('seedream.accurateTextAlt')" class="feature-image-preview"
                                        loading="lazy" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Ultra-Fast 4K Output Feature (reverse layout) -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/cms/019b5925a4fab4e828d7d1be8a88d7b3.webp"
                                        :alt="t('seedream.ultraFast4KAlt')" class="feature-image-preview"
                                        loading="lazy" />
                                </div>
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('seedream.ultraFast4KTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('seedream.ultraFast4KDesc') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- How to Use Section -->
        <section class="how-to-use-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('seedream.howToUseTitle') }}
                    </h2>
                    <p class="section-subtitle">
                        {{ t('seedream.howToUseSubtitle') }}
                    </p>
                </div>

                <ol class="steps-container">
                    <li class="step-item">
                        <div class="step-number">1</div>
                        <h3 class="step-title">
                            {{ t('seedream.step1Title') }}
                        </h3>
                        <p class="step-description">
                            {{ t('seedream.step1Description') }}
                        </p>
                    </li>

                    <li class="step-item">
                        <div class="step-number">2</div>
                        <h3 class="step-title">
                            {{ t('seedream.step2Title') }}
                        </h3>
                        <p class="step-description">
                            {{ t('seedream.step2Description') }}
                        </p>
                    </li>

                    <li class="step-item">
                        <div class="step-number">3</div>
                        <h3 class="step-title">
                            {{ t('seedream.step3Title') }}
                        </h3>
                        <p class="step-description">
                            {{ t('seedream.step3Description') }}
                        </p>
                    </li>
                </ol>
            </div>
        </section>

        <!-- YouTube Videos Section -->
        <YouTubeCarouselSection :title="t('seedream.youtubeTitle')" :videos="youtubeVideos" />

        <!-- X Posts Section -->
        <TwitterPosts :title="t('seedream.xPostsTitle')" :tweet-urls="twitterTweetUrls" />

        <!-- Explore Other Models Section -->
        <section class="explore-models-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('seedream.exploreModelsTitle') }}
                    </h2>
                </div>

                <div class="models-grid">
                    <NuxtLink to="/image/nano-banana" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/3ed8c9e1a1ec3b37133c608c292821cb.png"
                                :alt="t('seedream.model1Title')" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('seedream.model1Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/image/flux" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/a4d5bf7d150f6738f42d3d628ac39869.png"
                                :alt="t('seedream.model2Title')" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('seedream.model2Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/image/imagen" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/8ec333d0fa59f3ebf4a2373592ae76a6.png"
                                :alt="t('seedream.model3Title')" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('seedream.model3Title') }}
                        </h3>
                    </NuxtLink>
                </div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section class="faq-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">{{ t('seedream.faqTitle') }}</h2>
                </div>

                <div class="faq-list">
                    <div v-for="(faq, index) in faqData" :key="index" class="faq-item"
                        :class="{ active: activeFaqIndex === index }">
                        <div class="faq-question" @click="toggleFaq(index)">
                            <!-- <span class="faq-icon">></span> -->
                            <span class="faq-text">{{ t(faq.question) }}</span>
                        </div>
                        <div class="faq-answer">
                            <p>{{ t(faq.answer) }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA Section -->
        <section class="cta-section">
            <div class="container">
                <div class="cta-content">
                    <!-- 微鲤AI Logo -->
                    <div class="cta-logo">
                        <div class="logo-icon logo-image">
                            <img src="https://static.weryai.com/growth/image/4608e0a35205ff929dba2ea3c51f6e62.webp"
                                alt="微鲤AI" class="logo-image" loading="lazy" />
                        </div>
                    </div>

                    <!-- Main Content -->
                    <div class="cta-header">
                        <h2 class="cta-title">{{ t('seedream.ctaTitle') }}</h2>
                        <p class="cta-description">
                            {{ t('seedream.ctaDescription') }}
                        </p>
                    </div>

                    <!-- CTA Button -->
                    <div class="cta-actions">
                        <button @click="scrollToGenerator" class="cta-primary-btn">
                            {{ t('seedream.ctaButton') }}
                        </button>

                        <p class="cta-disclaimer">
                            {{ t('seedream.ctaDisclaimer') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useHead, useRequestURL } from '#imports'
import ImageGenerator from '~/components/ImageGenerator.vue'
import TwitterPosts from '~/components/TwitterPosts.vue'
import YouTubeCarouselSection from '~/components/model/video/YouTubeCarouselSection.vue'
import { useI18n } from '~/composables/useI18n'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { analyticsEvent } from '~/utils/log-event-func'

const { t } = useI18n()
const requestUrl = useRequestURL()
const requestUrlHref = requestUrl.href

definePageMeta({
    layout: 'website',
})

useMultilingualSeo({
    pageType: 'image_seendream',
})

// BreadcrumbList SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('seedream.generatorTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})

// Ref for generator section
const generatorSection = ref<HTMLElement | null>(null)

// Scroll to generator function
const scrollToGenerator = () => {
    if (generatorSection.value) {
        generatorSection.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}

// YouTube Videos Data
const youtubeVideos = ref([
    {
        title: 'Seedream Video 1',
        embedUrl: 'https://www.youtube.com/embed/I6HaR7nJcy4',
    },
    {
        title: 'Seedream Video 2',
        embedUrl: 'https://www.youtube.com/embed/fYVXpDNlSVs',
    },
    {
        title: 'Seedream Video 3',
        embedUrl: 'https://www.youtube.com/embed/APoss7LymdU',
    },
    {
        title: 'Seedream Video 4',
        embedUrl: 'https://www.youtube.com/embed/LRzdO0pE4dM',
    },
    {
        title: 'Seedream Video 5',
        embedUrl: 'https://www.youtube.com/embed/yMotu2o7U8w',
    },
])

// Twitter Tweet URLs Data
const twitterTweetUrls = [
    'https://twitter.com/Angaisb_/status/1965763685647110332',
    'https://twitter.com/ml_angelopoulos/status/1966187235088413176',
    'https://twitter.com/ruiwang2uiuc/status/1913540113088631295',
    'https://twitter.com/mark_k/status/1965510447693807854',
    'https://twitter.com/D_studioproject/status/1965709887658500443',
    'https://twitter.com/HalimAlrasihi/status/1965439882152821169',
    'https://twitter.com/replicate/status/1965503376940302740',
    'https://twitter.com/gcmouli/status/1951332999359840754',
    'https://twitter.com/fofrAI/status/1965386520053121187',
    'https://twitter.com/gowthami_s/status/1965632390963097712',
    'https://twitter.com/JieWu_10/status/1912565147773858099',
    'https://twitter.com/janekm/status/1965895674542879139',
]

// HowTo SEO - 为"如何使用"步骤添加结构化数据
const howToSteps = computed(() => [
    {
        title: t('seedream.step1Title'),
        description: t('seedream.step1Description'),
    },
    {
        title: t('seedream.step2Title'),
        description: t('seedream.step2Description'),
    },
    {
        title: t('seedream.step3Title'),
        description: t('seedream.step3Description'),
    },
])

// 注入 HowTo 结构化数据（支持响应式）
useHowToSeo(
    computed(() => t('seedream.howToUseTitle')),
    howToSteps
)

// FAQ Data
const faqData = ref([
    {
        question: 'seedream.faq1Question',
        answer: 'seedream.faq1Answer',
    },
    {
        question: 'seedream.faq2Question',
        answer: 'seedream.faq2Answer',
    },
    {
        question: 'seedream.faq3Question',
        answer: 'seedream.faq3Answer',
    },
    {
        question: 'seedream.faq4Question',
        answer: 'seedream.faq4Answer',
    },
    {
        question: 'seedream.faq5Question',
        answer: 'seedream.faq5Answer',
    },
])

// FAQ Interactive Functions
const activeFaqIndex = ref<number | null>(null)

const toggleFaq = (index: number) => {
    if (activeFaqIndex.value === index) {
        activeFaqIndex.value = null
    } else {
        activeFaqIndex.value = index
    }
}

// Image Generation Handler
const handleImageGenerate = (data: {
    prompt: string
    image?: File
    tabType: 'text-to-image' | 'image-to-image'
}) => {
    console.log('Image generation data:', data)
    // Add actual image generation logic here
    // For example, call API, show loading state, etc
}

onMounted(() => {
    analyticsEvent('view_model_page_common', {
        model_name: 'seedream_4_0',
        type: 'image',
    })
})
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';
</style>
